import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Plus,
  Search,
  CheckCircle2,
  Circle,
  Clock,
  Archive,
  MessageSquare,
  ListTodo,
  ArrowUpCircle,
  ArrowRightCircle,
  ArrowDownCircle,
  Filter,
  Globe,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useTaskBoard, type TaskWithMeta } from "@/hooks/useTaskBoard";
import { useWebsites } from "@/hooks/useWebsites";
import { useAuth } from "@/hooks/useAuth";

interface TaskListProps {
  onSelectTask: (taskId: string) => void;
  onNewTask: () => void;
  selectedTaskId: string | null;
}

type TabFilter = "open" | "in_progress" | "done";

const statusConfig = {
  open: { label: "Otwarte", icon: Circle, color: "text-blue-500" },
  in_progress: { label: "W trakcie", icon: Clock, color: "text-orange-500" },
  done: { label: "Zrobione", icon: CheckCircle2, color: "text-green-500" },
} as const;

const priorityConfig = {
  high: { label: "Wysoki", icon: ArrowUpCircle, color: "text-red-500", bg: "bg-red-50 text-red-700 border-red-200" },
  medium: { label: "Sredni", icon: ArrowRightCircle, color: "text-yellow-500", bg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  low: { label: "Niski", icon: ArrowDownCircle, color: "text-blue-400", bg: "bg-blue-50 text-blue-700 border-blue-200" },
} as const;

export default function TaskList({
  onSelectTask,
  onNewTask,
  selectedTaskId,
}: TaskListProps) {
  const { tasks, loading, updateTaskStatus } = useTaskBoard();
  const { websites } = useWebsites();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabFilter>("open");
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState<string | null>(null);

  const filteredTasks = tasks.filter((t) => {
    const matchesTab = activeTab === "done" ? t.status === "done" : t.status === activeTab;
    const matchesSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesPriority = !priorityFilter || t.priority === priorityFilter;
    const matchesDomain = !domainFilter || t.website_id === domainFilter;
    return matchesTab && matchesSearch && matchesPriority && matchesDomain;
  });

  const counts = {
    open: tasks.filter((t) => t.status === "open").length,
    in_progress: tasks.filter((t) => t.status === "in_progress").length,
    done: tasks.filter((t) => t.status === "done").length,
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "przed chwila";
    if (minutes < 60) return `${minutes} min temu`;
    if (hours < 24) return `${hours} godz. temu`;
    if (days < 7) return `${days} dni temu`;
    return date.toLocaleDateString("pl-PL");
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Taby statusow */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {(["open", "in_progress", "done"] as TabFilter[]).map((tab) => {
            const config = statusConfig[tab];
            const Icon = config.icon;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                {config.label}
                <span className="text-xs opacity-60">({counts[tab]})</span>
              </button>
            );
          })}
        </div>
        <Button size="sm" onClick={onNewTask}>
          <Plus className="h-4 w-4 mr-1" />
          Nowy task
        </Button>
      </div>

      {/* Szukaj + filtr priorytetu */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj taskow..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setPriorityFilter(null)}>
              Wszystkie priorytety
            </DropdownMenuItem>
            {(["high", "medium", "low"] as const).map((p) => {
              const config = priorityConfig[p];
              const Icon = config.icon;
              return (
                <DropdownMenuItem key={p} onClick={() => setPriorityFilter(p)}>
                  <Icon className={`h-4 w-4 mr-2 ${config.color}`} />
                  {config.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        {websites.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={domainFilter ? "default" : "outline"} size="icon">
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setDomainFilter(null)}>
                Wszystkie domeny
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {websites.map((w) => (
                <DropdownMenuItem key={w.id} onClick={() => setDomainFilter(w.id)}>
                  <Globe className="h-3.5 w-3.5 mr-2 text-violet-500" />
                  {w.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Lista */}
      {filteredTasks.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <ListTodo className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {activeTab === "done"
                ? "Brak ukonczonych taskow"
                : search
                ? "Nie znaleziono taskow"
                : "Brak taskow. Utworz nowy!"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              isSelected={task.id === selectedTaskId}
              isOwner={task.user_id === user?.id}
              onClick={() => onSelectTask(task.id)}
              onStatusChange={updateTaskStatus}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCard({
  task,
  isSelected,
  isOwner,
  onClick,
  onStatusChange,
  formatDate,
}: {
  task: TaskWithMeta;
  isSelected: boolean;
  isOwner: boolean;
  onClick: () => void;
  onStatusChange: (id: string, status: "open" | "in_progress" | "done") => Promise<{ error: string | null }>;
  formatDate: (d: string) => string;
}) {
  const pConfig = priorityConfig[task.priority];
  const sConfig = statusConfig[task.status];
  const StatusIcon = sConfig.icon;
  const progress =
    task.items_total > 0
      ? Math.round((task.items_done / task.items_total) * 100)
      : null;

  const handleStatusClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const next =
      task.status === "open"
        ? "in_progress"
        : task.status === "in_progress"
        ? "done"
        : "open";
    await onStatusChange(task.id, next as "open" | "in_progress" | "done");
  };

  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? "ring-2 ring-primary shadow-md" : "hover:border-primary/30"
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Status icon - klikniecie zmienia status */}
          <button
            onClick={handleStatusClick}
            className={`mt-0.5 shrink-0 transition-colors hover:scale-110 ${sConfig.color}`}
            title={`Zmien status (teraz: ${sConfig.label})`}
          >
            <StatusIcon className="h-5 w-5" />
          </button>

          <div className="flex-1 min-w-0">
            {/* Tytul + priorytet */}
            <div className="flex items-center gap-2 mb-1">
              <h3
                className={`font-medium text-sm truncate ${
                  task.status === "done" ? "line-through text-muted-foreground" : ""
                }`}
              >
                {task.title}
              </h3>
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${pConfig.bg}`}>
                {pConfig.label}
              </Badge>
              {task.website_name && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 bg-violet-50 text-violet-700 border-violet-200 gap-0.5">
                  <Globe className="h-2.5 w-2.5" />
                  {task.website_name}
                </Badge>
              )}
            </div>

            {/* Opis */}
            {task.description && (
              <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
                {task.description}
              </p>
            )}

            {/* Progress bar checklisty */}
            {progress !== null && (
              <div className="flex items-center gap-2 mb-2">
                <Progress value={progress} className="h-1.5 flex-1" />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {task.items_done}/{task.items_total}
                </span>
              </div>
            )}

            {/* Meta */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(task.updated_at)}
              </span>
              {task.comment_count > 0 && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {task.comment_count}
                </span>
              )}
              {task.items_total > 0 && (
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {task.items_done}/{task.items_total} pozycji
                </span>
              )}
              {isOwner && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  Twoj
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
